import React, { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createApprovalToken } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { userError } from "@/lib/userError";

/**
 * Renders a trigger that requires explicit owner approval. On confirm it creates a
 * short-lived single-use approval token, then runs `perform(token)`.
 */
export const ApprovalButton = ({
  action, contactId, purpose, resourceId, perform, title, description,
  triggerLabel, confirmLabel = "Hyväksy", testid, className, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const { token } = await createApprovalToken({
        action, contact_id: contactId, purpose, resource_id: resourceId,
      });
      await perform(token);
    } catch (e) {
      const msg = userError(e, "Toiminto epäonnistui. Tarkista tallennuksen tila ennen uudelleenyritystä.");
      toast.error(msg);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform active:scale-95 disabled:opacity-50",
          className
        )}
      >
        {triggerLabel}
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`${testid}-dialog`} className="rounded-2xl max-w-sm mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading">
              <ShieldCheck className="h-5 w-5 text-primary" />
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left whitespace-pre-line">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-cancel`}>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${testid}-confirm`}
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              disabled={busy}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
